/**
 * 6-blok — mobil ilovani yuklab olish (ЧТЗ 4.2.11.1, 2.6-band).
 *
 * Android — Google Play, iOS — App Store havolalari `PortalSettings` da
 * (superadmin paneli). Dinamik QR-kod do'kon manzilini emas, portalning qisqa
 * havolasini saqlaydi: telefon kamerasi bilan skanerlanganda qurilma turi
 * aniqlanib, mos do'kon ochiladi — do'kon manzili o'zgarsa ham chop etilgan
 * QR eskirmaydi. «Bepul», «O'zbekcha» kabi belgilar ham CMS'dan keladi.
 */
import { useState } from "react";

import type { PublicHome } from "@/shared/api/types";
import { mobileAppFallback } from "@/shared/data/landing";
import { translate } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { Icon } from "@/shared/ui/Icon";

import { SplitHead } from "./SplitHead";

const QR_FALLBACK = "/api/v1/portal/public/mobile-app/qr.svg";

export function StoreButton({
  href,
  store,
  label,
}: {
  href: string;
  store: "google" | "apple";
  label: string;
}) {
  const icon =
    store === "google" ? (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M4 2.8v18.4c0 .3.3.5.6.3L20.4 12.6c.3-.2.3-.6 0-.8L4.6 2.5c-.3-.2-.6 0-.6.3z" />
      </svg>
    ) : (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.9-3.5.9-.7 0-1.8-.9-3-.8-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.6 1.3-2.7 0 0-2.5-1-2.5-3.7zM14.1 5.8c.6-.8 1.1-1.8 1-2.8-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.7-1 2.7 1 .1 2-.5 2.7-1.3z"
        />
      </svg>
    );
  /* Do'konga chiqqunga qadar Android havolasi to'g'ridan-to'g'ri APK faylga qo'yilishi mumkin
     (`PortalSettings.google_play_url`) — u holda tugma faylni yuklab oladi. */
  const apk = store === "google" && /\.apk$/i.test(href.split(/[?#]/)[0]);
  const content = (
    <>
      {apk ? (
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="currentColor" d="M12 3a1 1 0 0 1 1 1v9.6l3.3-3.3a1 1 0 1 1 1.4 1.4l-5 5a1 1 0 0 1-1.4 0l-5-5a1 1 0 1 1 1.4-1.4l3.3 3.3V4a1 1 0 0 1 1-1zM5 19h14a1 1 0 1 1 0 2H5a1 1 0 1 1 0-2z" />
        </svg>
      ) : (
        icon
      )}
      <span>
        <small>{store === "google" ? "Android uchun" : "iPhone uchun"}</small>
        <b>{apk ? "APK yuklab olish" : label}</b>
      </span>
    </>
  );
  if (!href) {
    return (
      <span className="store-btn is-disabled" aria-disabled="true" title="Havola tez orada qo'shiladi">
        {content}
        <em className="store-soon">Tez orada</em>
      </span>
    );
  }
  if (apk) {
    return (
      <a className="store-btn" href={href} download>
        {content}
      </a>
    );
  }
  return (
    <a className="store-btn" href={href} target="_blank" rel="noreferrer noopener">
      {content}
    </a>
  );
}

export function MobileAppSection({ home }: { home: PublicHome | null }) {
  const { t, lang } = useI18n();
  const settings = home?.settings;
  const [qrFailed, setQrFailed] = useState(false);

  if (settings && settings.mobile_app_enabled === false) return null;

  const title = translate(settings?.mobile_app_title, lang) || mobileAppFallback.title;
  const lead = translate(settings?.mobile_app_text, lang) || mobileAppFallback.text;
  const badges = settings?.mobile_app_badges?.length ? settings.mobile_app_badges : mobileAppFallback.badges;
  const qr = home?.mobile_app?.qr_url || QR_FALLBACK;

  return (
    <section className="section mobile-app-section" id="mobile-app" aria-labelledby="app-title">
      <div className="container">
        {/* Boshqa sahifalar kabi: matn chapda, mavzuli 3D model (smartfon) o'ngda. */}
        <SplitHead scene="mobile" eyebrow={t("nav.app")} title={title} titleId="app-title" lead={lead} />

        <div className="mobile-app" data-reveal>
          <div className="mobile-app-copy">
            <ul className="app-badges" aria-label="Ilova xususiyatlari">
              {badges.map((badge) => (
                <li key={badge} className="badge badge-brand">
                  <Icon name="check" size={12} />
                  {badge}
                </li>
              ))}
            </ul>
            <ul className="check-list">
              {mobileAppFallback.features.map((feature) => (
                <li key={feature}>
                  <Icon name="checkCircle" size={18} />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="store-buttons">
              <StoreButton href={settings?.google_play_url ?? ""} store="google" label="Google Play" />
              <StoreButton href={settings?.app_store_url ?? ""} store="apple" label="App Store" />
            </div>
          </div>

          <div className="mobile-app-visual is-qr-only">
            <figure className="qr-card">
              {qrFailed ? (
                <div className="qr-placeholder" role="img" aria-label="QR-kod vaqtincha mavjud emas">
                  <Icon name="smartphone" size={32} />
                  <span>QR-kod server orqali yaratiladi</span>
                </div>
              ) : (
                <img
                  src={qr}
                  alt="Mobil ilovani yuklab olish uchun QR-kod"
                  width={148}
                  height={148}
                  loading="lazy"
                  onError={() => setQrFailed(true)}
                />
              )}
              <figcaption>Telefon kamerasi bilan skanerlang</figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
